import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

let _cachedRole = null;
let _cacheAt = 0;

const ROLE_HOME = {
  super_admin: '/admin',
  center_manager: '/admin',
  center_admin: '/admin',
  head_office: '/headoffice',
  headoffice: '/headoffice',
  headoffice_admin: '/headoffice',
  accounts: '/accounts',
  accounts_admin: '/accounts',
  hr: '/hr',
  hr_admin: '/hr',
  teacher: '/teacher',
  parent: '/parent',
  student: '/student',
  partner: '/partner',
  presales: '/presales',
};

// Root dashboards where the button MUST hide (we're already home).
const HIDE_ON_EXACT = new Set([
  '/', '/login',
  '/admin', '/admin/login',
  '/teacher', '/teacher/login',
  '/parent', '/parent/login',
  '/student', '/student/login',
  '/headoffice', '/headoffice/login',
  '/accounts', '/accounts/login',
  '/hr', '/hr/login',
  '/hrms/login',
  '/partner', '/partner/login',
  '/presales', '/presales/login',
  '/auth/callback',
  '/dashboard',
]);

/**
 * Iter 336 — Global "Back to Home" pill that shows on every inner page and
 * hides on portal root dashboards. Positioned at fixed top-left so it never
 * overlaps existing page-level Back arrows. Portal-aware target based on
 * `/api/auth/me` role.
 */
export default function GlobalHomeButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const [role, setRole] = React.useState(_cachedRole || '');

  React.useEffect(() => {
    if (_cachedRole && Date.now() - _cacheAt < 5 * 60 * 1000) {
      setRole(_cachedRole);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        const rl = r?.data?.role || '';
        if (!cancelled) {
          _cachedRole = rl;
          _cacheAt = Date.now();
          setRole(rl);
        }
      } catch (_) { /* not signed in — component still renders on inner pages, but harmlessly */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const path = (location.pathname || '/').replace(/\/+$/, '') || '/';
  if (HIDE_ON_EXACT.has(path)) return null;
  // Hide on any login sub-route just in case a portal introduces new login paths
  if (path.endsWith('/login') || path.includes('/login/')) return null;

  const target = ROLE_HOME[role] || '/';

  return (
    <button
      type="button"
      onClick={() => navigate(target)}
      className="fixed top-3 left-3 z-[9999] flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900/85 hover:bg-slate-800 border border-slate-700/70 text-slate-200 text-xs font-medium shadow-lg backdrop-blur-md transition-all"
      title="Back to Home"
      data-testid="global-home-btn"
    >
      <Home className="w-3.5 h-3.5" />
      <span>Home</span>
    </button>
  );
}
